import React from 'react';
import styled from 'styled-components';
import { useDrag } from 'react-dnd';

import { Tooltip, Container } from 'ui/components/styled';
import { Dot } from 'ui/components/shared';
import { spacing } from 'ui/theme/utils';
import { useUser } from 'hooks';
import { truncate } from 'helpers/parser';

export const JobDot = ({
  job,
  isDraggable = false,
  hasTooltip = false,
  onTooltipReadMoreClick,
  ...rest
}) => {
  const tooltipRef = React.useRef();
  const [active, setActive] = React.useState(false);
  const { redirectTo } = useUser();
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'job',
      item: job,
      canDrag: () => isDraggable,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [job, isDraggable]
  );
  const disabled = isDragging || !isDraggable;

  const handleClick = (event) => {
    event.preventDefault();
    if (disabled) return;

    redirectTo(`/jobs/${job?.id}/edit`);
  };

  const handleTooltipReadMoreClick = () => {
    tooltipRef.current.changeVisibility(false);
    if (onTooltipReadMoreClick) {
      onTooltipReadMoreClick();
    }
  };

  const renderTooltipContent = () => (
    <>
      <TooltipTitle title={job?.name}>{job?.name}</TooltipTitle>
      {job?.lastEmail && (
        <>
          <TooltipText mt="xxs">
            {truncate(job.lastEmail.bodyAsText || job.lastEmail.body)}
          </TooltipText>
          <Container flexDirection="row" justifyContent="flex-end" height="auto" mt="xxs">
            <TooltipReadMore onClick={handleTooltipReadMoreClick} />
          </Container>
        </>
      )}
    </>
  );

  if (isDragging) {
    return null;
  }

  return (
    <Tooltip
      ref={tooltipRef}
      wrap={hasTooltip}
      content={renderTooltipContent()}
      width="160px"
      maxWidth="430px"
      wrapperProps={{ float: 'left', mr: 'xxs', mb: 'xxs' }}
      contentProps={{ px: 's', py: 'xs' }}
      onVisibilityChange={setActive}
    >
      <Dot
        ref={drag}
        role="button"
        status={job?.status}
        isForceGreen={job?.isForceGreen}
        onClick={handleClick}
        disabled={disabled}
        active={active}
        {...rest}
      />
    </Tooltip>
  );
};

const TooltipTitle = styled.h3`
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.04em;
  text-align: left;
  color: ${({ theme }) => theme.colors.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TooltipText = styled.div`
  font-weight: normal;
  font-size: 13px;
  letter-spacing: 0.04em;
  line-height: 20px;
  text-align: left;
  color: ${({ theme }) => theme.colors.grey};
  overflow: auto;
  max-width: 400px;
  max-height: 200px;
  word-wrap: break-word;
  white-space: pre-wrap;
  ${(props) => spacing(props)}
`;

const TooltipReadMore = styled.button.attrs(({ children = 'Read more ...' }) => ({
  children,
}))`
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.04em;
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
`;
